import type { Command } from "../document/commands.ts";
import { EMPTY_TILE, packedToTileRef } from "../document/packed-tile.ts";
import type { CollisionLayer, DoorLayer, LayerDocument, MapDocument, TerrainLayer, TileLayer, TilesetRef, ZoneLayer } from "../document/types.ts";
import { areaGridBounds, areaGridScale, areaGridSize, shiftedRect, type AreaGrid, type AreaOffset, type AreaRect } from "./area.ts";

/**
 * The grids of an area's layers, read raw: a tile layer's packed tiles, a terrain layer's corner bytes and the
 * quarter-cell bytes of a collision, zone or door layer. Within one layer a byte names the same zone type or door
 * wherever it stands, so an area's cells can be moved and cleared as raw values and named by id only when a
 * command is built.
 *
 * A move writes each cell of the union of the source and the destination once, with its final value (ADR-128
 * decisions 19 to 23): a destination cell takes what the matching source cell held, empties included; a source
 * cell the destination does not cover is emptied. A terrain layer's outer ring of corners follows decision 21: at
 * the source a set edge corner next to which the layer paints a corner outside the rectangle (one of the eight
 * around it, the test Replace in selection makes) stays set, and at the destination an edge corner the block holds
 * as empty keeps what the corner holds once the source is emptied, so a corner set only by the block's own
 * departing paint counts as empty. A clear has no destination and empties every cell of the rectangle, a terrain
 * layer's edge corners included (decision 33): what it clears is what the area counts as held, and paint that runs
 * on past the rectangle ends at its edge.
 */

/**
 * A layer that holds a grid.
 */
export type GridLayer = TileLayer | TerrainLayer | CollisionLayer | ZoneLayer | DoorLayer;

/**
 * A layer's grid read raw: which grid, its cells and the value of an empty cell.
 */
export interface RawGrid {
  readonly grid: AreaGrid;
  readonly cells: Readonly<Uint8Array> | Readonly<Uint32Array>;
  readonly empty: number;
}

/**
 * Whether a layer holds a grid.
 *
 * @param layer The layer.
 * @returns True for a tile, terrain, collision, zone or door layer.
 */
export function isGridLayer(layer: LayerDocument): layer is GridLayer {
  return layer.kind !== "prop" && layer.kind !== "entity";
}

/**
 * A layer's grid read raw.
 *
 * @param layer The layer.
 * @returns Its grid, cells and empty value.
 */
export function rawGridOf(layer: GridLayer): RawGrid {
  switch (layer.kind) {
    case "tile":
      return { grid: "tiles", cells: layer.tiles, empty: EMPTY_TILE };
    case "terrain":
      return { grid: "corners", cells: layer.corners, empty: 0 };
    default:
      return { grid: "quarters", cells: layer.cells, empty: 0 };
  }
}

/**
 * Calls a function for every cell of a rectangle of a grid, row by row.
 *
 * @param bounds The rectangle, in the grid's columns and rows, both corners inclusive.
 * @param width The grid's width.
 * @param visit Called with each cell's index, column and row.
 */
export function eachCell(bounds: AreaRect, width: number, visit: (index: number, x: number, y: number) => void): void {
  for (let y = bounds.y0; y <= bounds.y1; y += 1) {
    for (let x = bounds.x0; x <= bounds.x1; x += 1) {
      visit(y * width + x, x, y);
    }
  }
}

/**
 * How many cells of a layer's grid inside a rectangle of tiles hold something.
 *
 * @param map The map.
 * @param layer The layer.
 * @param rect The rectangle of tiles.
 * @returns The painted cells, corners or quarter cells.
 */
export function paintedIn(map: MapDocument, layer: GridLayer, rect: AreaRect): number {
  const raw = rawGridOf(layer);
  let count = 0;
  eachCell(areaGridBounds(rect, raw.grid), areaGridSize(map, raw.grid).width, (index) => {
    count += raw.cells[index] === raw.empty ? 0 : 1;
  });
  return count;
}

const DOOR_TOTALS = new WeakMap<Readonly<Uint8Array>, Uint32Array>();

/**
 * How many cells each byte of a door layer's grid paints, counted once for a grid: a layer's cells are replaced,
 * never changed in place, so the grid itself is the key.
 *
 * @param cells The layer's cells.
 * @returns The count for each byte, 0 to 255.
 */
function doorTotals(cells: Readonly<Uint8Array>): Uint32Array {
  const held = DOOR_TOTALS.get(cells);
  if (held !== undefined) {
    return held;
  }
  const totals = new Uint32Array(256);
  for (let index = 0; index < cells.length; index += 1) {
    totals[cells[index] as number] = (totals[cells[index] as number] as number) + 1;
  }
  DOOR_TOTALS.set(cells, totals);
  return totals;
}

/**
 * Where each door of a door layer is painted against a rectangle of tiles: inside it, outside it, both or neither.
 * Only the rectangle's own cells are read each time: a door is painted outside when the layer paints more of its
 * cells than the rectangle holds, by the layer's totals, which are counted once for a grid.
 *
 * @param map The map.
 * @param layer The door layer.
 * @param rect The rectangle of tiles.
 * @returns For each door of the layer's list, in its order, whether a cell inside and a cell outside name it.
 */
export function doorSides(map: MapDocument, layer: DoorLayer, rect: AreaRect): { readonly inside: boolean; readonly outside: boolean }[] {
  const width = areaGridSize(map, "quarters").width;
  const bounds = areaGridBounds(rect, "quarters");
  const totals = doorTotals(layer.cells);
  const inside = new Uint32Array(256);
  for (let y = bounds.y0; y <= bounds.y1; y += 1) {
    for (let x = bounds.x0; x <= bounds.x1; x += 1) {
      const byte = layer.cells[y * width + x] as number;
      inside[byte] = (inside[byte] as number) + 1;
    }
  }
  return layer.doors.map((_door, i) => ({ inside: (inside[i + 1] as number) > 0, outside: (totals[i + 1] as number) > (inside[i + 1] as number) }));
}

/**
 * One cell a plan changes, raw: its index in the layer's grid and the values before and after.
 */
export interface RawChange {
  readonly index: number;
  readonly before: number;
  readonly after: number;
}

/**
 * What a move or a clear does to one grid layer: the cells it changes, in index order, and the destination cells
 * whose paint it replaces (decisions 19 and 20): cells that hold something once the source is emptied and that the
 * move would change, in index order.
 */
export interface GridPlan {
  readonly changes: readonly RawChange[];
  readonly replaced: readonly number[];
}

/**
 * Whether a grid cell lies in a rectangle of the grid.
 *
 * @param bounds The rectangle, both corners inclusive.
 * @param x The column.
 * @param y The row.
 * @returns True when inside.
 */
function inside(bounds: AreaRect, x: number, y: number): boolean {
  return x >= bounds.x0 && x <= bounds.x1 && y >= bounds.y0 && y <= bounds.y1;
}

/**
 * Whether a grid cell lies on a rectangle's outer ring.
 *
 * @param bounds The rectangle.
 * @param x The column, inside the rectangle.
 * @param y The row, inside the rectangle.
 * @returns True on the ring.
 */
function onEdge(bounds: AreaRect, x: number, y: number): boolean {
  return x === bounds.x0 || x === bounds.x1 || y === bounds.y0 || y === bounds.y1;
}

/**
 * The columns of one row a plan visits: the source's and the destination's spans on that row, merged, in order.
 *
 * @param y The row.
 * @param spans The rectangles whose spans count.
 * @returns The spans, each first and last column inclusive, ordered and not overlapping.
 */
function spansOf(y: number, spans: readonly AreaRect[]): [number, number][] {
  const rows = spans.filter((b) => y >= b.y0 && y <= b.y1).map((b): [number, number] => [b.x0, b.x1]);
  rows.sort((a, b) => a[0] - b[0]);
  const merged: [number, number][] = [];
  for (const span of rows) {
    const last = merged[merged.length - 1];
    if (last !== undefined && span[0] <= last[1] + 1) {
      last[1] = Math.max(last[1], span[1]);
    } else {
      merged.push([span[0], span[1]]);
    }
  }
  return merged;
}

/**
 * Plans a move or a clear of one grid layer's cells in a rectangle of tiles.
 *
 * @param map The map.
 * @param layer The layer.
 * @param rect The source rectangle of tiles, inside the map.
 * @param offset The move's offset in whole tiles, its destination inside the map; null for a clear, which empties
 *   every cell of the rectangle.
 * @returns The cells changed and the destination paint replaced.
 */
export function planGrid(map: MapDocument, layer: GridLayer, rect: AreaRect, offset: AreaOffset | null): GridPlan {
  const raw = rawGridOf(layer);
  const cells = raw.cells;
  const { width, height } = areaGridSize(map, raw.grid);
  const scale = areaGridScale(raw.grid);
  const corners = raw.grid === "corners";
  const source = areaGridBounds(rect, raw.grid);
  const shift = offset === null ? 0 : offset.dx * scale + offset.dy * scale * width;
  const target = offset === null ? null : shiftedRect(source, { dx: offset.dx * scale, dy: offset.dy * scale });
  /**
   * Whether the layer paints a corner outside the source next to one.
   *
   * @param x The corner's column.
   * @param y The corner's row.
   * @returns True when one of its eight neighbours outside the source is set.
   */
  const carriesOn = (x: number, y: number): boolean => {
    for (let ny = y - 1; ny <= y + 1; ny += 1) {
      for (let nx = x - 1; nx <= x + 1; nx += 1) {
        if (nx >= 0 && ny >= 0 && nx < width && ny < height && !inside(source, nx, ny) && cells[ny * width + nx] !== 0) {
          return true;
        }
      }
    }
    return false;
  };
  const changes: RawChange[] = [];
  const replaced: number[] = [];
  const rows = target === null ? [source] : [source, target];
  for (let y = Math.min(...rows.map((b) => b.y0)); y <= Math.max(...rows.map((b) => b.y1)); y += 1) {
    for (const [first, last] of spansOf(y, rows)) {
      for (let x = first; x <= last; x += 1) {
        const index = y * width + x;
        const held = cells[index] as number;
        const left = !inside(source, x, y) ? held : target !== null && corners && held !== 0 && onEdge(source, x, y) && carriesOn(x, y) ? held : raw.empty;
        let after = left;
        if (target !== null && inside(target, x, y)) {
          const block = cells[index - shift] as number;
          after = !corners || !onEdge(target, x, y) || block !== 0 ? block : left;
          if (left !== raw.empty && after !== left) {
            replaced.push(index);
          }
        }
        if (after !== held) {
          changes.push({ index, before: held, after });
        }
      }
    }
  }
  return { changes, replaced };
}

/**
 * A zone layer's byte as the type id a command carries.
 *
 * @param layer The layer.
 * @param value The byte.
 * @returns The id, or null for 0.
 * @throws Error when the layer's table names no type for the byte.
 */
function zoneId(layer: ZoneLayer, value: number): string | null {
  if (value === 0) {
    return null;
  }
  const type = layer.types[value - 1];
  if (type === undefined) {
    throw new Error(`zone layer ${layer.id} holds byte ${value}, which its table of ${layer.types.length} types does not name`);
  }
  return type;
}

/**
 * A door layer's byte as the door id a command carries.
 *
 * @param layer The layer.
 * @param value The byte.
 * @returns The id, or null for 0.
 * @throws Error when the layer's list holds no door for the byte.
 */
function doorId(layer: DoorLayer, value: number): string | null {
  if (value === 0) {
    return null;
  }
  const door = layer.doors[value - 1];
  if (door === undefined) {
    throw new Error(`door layer ${layer.id} holds byte ${value}, which its list of ${layer.doors.length} doors does not name`);
  }
  return door.id;
}

/**
 * The one command that writes a grid layer's changes, of the kind that paints that layer: `setTiles`,
 * `paintCorners`, `paintCollision`, `paintZone` or `paintDoor`, each cell carrying its value before and after.
 *
 * @param mapId The map.
 * @param layer The layer.
 * @param changes The cells, raw.
 * @param tilesets The world's tilesets, which name a packed tile's tileset.
 * @returns The command.
 * @throws Error when a byte names no zone type or door of its layer.
 */
export function gridCommand(mapId: string, layer: GridLayer, changes: readonly RawChange[], tilesets: readonly TilesetRef[]): Command {
  switch (layer.kind) {
    case "tile":
      return { kind: "setTiles", mapId, layerId: layer.id, cells: changes.map((c) => ({ index: c.index, before: packedToTileRef(c.before, tilesets), after: packedToTileRef(c.after, tilesets) })) };
    case "terrain":
      return { kind: "paintCorners", mapId, layerId: layer.id, cells: changes };
    case "collision":
      return { kind: "paintCollision", mapId, layerId: layer.id, cells: changes };
    case "zone":
      return { kind: "paintZone", mapId, layerId: layer.id, cells: changes.map((c) => ({ index: c.index, before: zoneId(layer, c.before), after: zoneId(layer, c.after) })) };
    case "door":
      return { kind: "paintDoor", mapId, layerId: layer.id, cells: changes.map((c) => ({ index: c.index, before: doorId(layer, c.before), after: doorId(layer, c.after) })) };
  }
}
